import { motion } from "framer-motion";
import { Badge, Button, cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import type { AccountUser } from "../../lib/auth";
import { Logo } from "../../components/Logo";
import { LanguageMenu } from "../../components/LanguageMenu";
import { ThemeToggle } from "../../components/ThemeToggle";
import { AccountMenu } from "../header/AccountMenu";
import type { TabKey } from "../tabs";
import type { NavBadges, NavItem } from "./nav";

/**
 * The portal's top bar: whose portal this is, the client's services, and the
 * person holding the session.
 *
 * ONE navigation, and it is the services. The hub draws two - services across
 * the top and the person's account screens down the side - which is right for
 * a place somebody manages a product from. A client arrives here to read an
 * invoice or sign a contract; their password and their notification settings
 * are things they change twice a year, so those live behind the avatar and the
 * whole width of the page goes to the document they came for.
 *
 * From `lg` up the services are words in this bar. Under `lg` they are the tab
 * bar at the bottom of the screen (`PortalTabBar`) and this bar keeps only the
 * wordmark and the avatar - one list at a time, never two.
 *
 * The person is the avatar alone, at every width. The bar is capped at
 * `max-w-6xl`, so a wider screen gives it no more room: seven services and the
 * name chip beside them ran the last link under the chip. The name and the
 * company are the first lines of the panel the avatar opens.
 */
export function PortalHeader({
  user,
  crmLink,
  active,
  nav,
  badges,
  menuTabs,
  caption,
  onGo,
  onLogout,
}: {
  /** The signed-in person, or null in a staff preview. */
  user: AccountUser | null;
  /** The staff CRM, for somebody the CRM would let in. */
  crmLink: boolean;
  active: TabKey;
  nav: NavItem[];
  badges: NavBadges;
  /** The personal screens the avatar menu may open. */
  menuTabs: TabKey[];
  /** The company this person represents, for the line under their name. */
  caption?: string;
  onGo: (tab: TabKey) => void;
  onLogout: () => void;
}) {
  const t = useT();
  // The statement is the one label that does not fit the bar at `lg` beside
  // six others; its short form is the same word the tab bar uses.
  const label = (key: TabKey) => (key === "statement" ? t("portal.nav.statement") : t(`tabs.${key}`));

  return (
    <header className="sticky top-0 z-20 border-b border-border bg-background/85 backdrop-blur print:hidden">
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-4 px-4 xl:gap-6">
        {/* The wordmark is the way home, as it is on every site the client
            knows. A kit button rather than a bare image, so the keyboard
            reaches it and it says where it leads. No ghost fill on hover:
            a grey box behind a logo reads as a stray panel, not as a link,
            so the mark only dims a little, like the links beside it. */}
        <Button
          variant="ghost"
          aria-label={t("tabs.overview")}
          onClick={() => onGo("overview")}
          className="-ml-2 h-11 shrink-0 px-2 transition-opacity hover:bg-transparent hover:opacity-80 active:scale-100"
        >
          <Logo variant="full" className="h-6 w-auto md:h-7" />
        </Button>

        <nav aria-label={t("portal.group")} className="hidden h-16 min-w-0 flex-1 items-stretch gap-0.5 lg:flex">
          {nav.map(({ key }) => {
            const on = active === key;
            const badge = badges[key];
            return (
              <Button
                key={key}
                variant="ghost"
                aria-current={on ? "page" : undefined}
                onClick={() => onGo(key)}
                className={cn(
                  "relative h-16 rounded-none px-2.5 text-[13px] hover:bg-transparent active:scale-100 xl:px-3 xl:text-sm",
                  "focus-visible:ring-inset focus-visible:ring-ring",
                  on ? "font-semibold text-foreground" : "text-foreground/70 hover:text-foreground",
                )}
              >
                {label(key)}
                {badge && badge.count > 0 && (
                  <Badge variant={badge.tone} className="px-2 tabular-nums">
                    {badge.count}
                    <span className="sr-only"> {badge.label}</span>
                  </Badge>
                )}
                {/* The active mark is a line on the bar's own bottom edge, and it
                    slides: one shared `layoutId`, alive in this row only. */}
                {on && (
                  <motion.span
                    layoutId="portal-nav-line"
                    className="absolute inset-x-2.5 -bottom-px h-0.5 rounded-full bg-primary xl:inset-x-3"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }}
                  />
                )}
              </Button>
            );
          })}
        </nav>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          {/* A preview has no signed-in person to open the menu for: the token
              names a client, and that panel is about whoever holds the
              session. The two switches it would carry stay loose instead. */}
          {user ? (
            <AccountMenu
              user={user}
              portalBrand
              adminLink={null}
              crmLink={crmLink}
              openTabs={menuTabs}
              caption={caption}
              compact
              allScreens
              onGo={onGo}
              onLogout={onLogout}
            />
          ) : (
            <>
              <LanguageMenu />
              <ThemeToggle />
            </>
          )}
        </div>
      </div>
    </header>
  );
}
